import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ACCOUNT_NUMBER_DEFAULT_VALUE } from 'appConstants';
import useOrderWizard from 'components/OrderWizard/useOrderWizard';
import { CustomersField } from 'fields';
import { useField } from 'formik';
import useFieldCustomers from 'hooks/useFieldCustomers';
import useMultiAccountNotification from 'hooks/useMultiAccountNotification';

import type { ICustomersSelect } from './types';

export default function Customers({ name, disabled }: ICustomersSelect) {
  const [{ value }, , { setValue }] = useField(name);
  const [{ value: multiAccount }, , { setValue: setMultiAccount }] = useField('multiAccount');
  const [, , { setValue: setDebtOwner }] = useField('debtOwner');
  const [{ value: products }, , { setValue: setProducts }] = useField('products');
  const { t } = useTranslation('CreateOrder');
  const { resetDeliveryDetails } = useOrderWizard();
  const displayMultiAccountNotification = useMultiAccountNotification('CreateOrder');

  const { cmpNewValueToOld, accountChangeDialog, loadAddressesFromOption } =
    useFieldCustomers('CreateOrder');

  const handleOnChange = useCallback(
    async (option) => {
      const newValueSameAsOld = cmpNewValueToOld(option, value);
      if (newValueSameAsOld) return;

      const confirmed = await accountChangeDialog(products, value);

      if (products && products.length && value.customerReference && !confirmed) return;

      await setValue(option || ACCOUNT_NUMBER_DEFAULT_VALUE, true);

      if (products && products.length) {
        await setProducts([]);
      }

      resetDeliveryDetails(); // Reset pre-populated fields

      loadAddressesFromOption(option);

      // Display multiple account off notification
      if (multiAccount) {
        displayMultiAccountNotification('Off');
        await setMultiAccount('');
        await setDebtOwner('false');
      }
    },
    [
      setMultiAccount,
      setDebtOwner,
      value,
      setValue,
      displayMultiAccountNotification,
      multiAccount,
      products,
      setProducts,
      resetDeliveryDetails,
      cmpNewValueToOld,
      accountChangeDialog,
      loadAddressesFromOption,
    ],
  );

  return (
    <CustomersField
      name={name}
      disabled={disabled}
      referenceKey="customerReference"
      textFieldProps={{
        label: t('CustomerDetails.labels.accountNumber'),
        placeholder: t('CustomerDetails.placeholders.accountNumber'),
        required: true,
      }}
      onChange={handleOnChange}
    />
  );
}
